import React, { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import NumberPadDialog from "../NumberPadDialog";
import SelectDialog from "../SelectDialog";
import InterruptWorkDetailDialod from "./InterruptWorkDetailDialod";
import GreenButton from "../forms/buttons/GreenButton";
import OutlineButton from "../forms/buttons/OutlineButton";
import ChipButton from "../forms/buttons/ChipButton";
import { constantService } from "../../services/ConstantService";
import { strings } from "../../Strings";
import { dateService } from "../../services/DateService";

const stopLossCategory = [
  { label: "よく使う", value: "popular" },
  { label: "設備", value: "equipment" },
  { label: "材料", value: "material" },
  { label: "品質", value: "quality" },
  { label: "その他", value: "other" },
];
const WorkBadge = ({ workType }) => {
  const target = strings.WORK_TYPE_OPTIONS.find((o) => o.value === workType);
  return (
    <span className={`work-badge ${target?.className}`}>{target?.label}</span>
  );
};
/**
 * 作業内容詳細テーブルコンポーネント（編集）
 * @param {object} workContents 作業内容
 * @param {Function} onChange 項目変更時の処理
 * @param {Function} onSave 内容保存時の処理
 * @param {Function} onFinish 作業完了時の処理
 * @param {object} errors エラーメッセージ
 * @class WorkContentEditTable
 */
export default function WorkContentEditTable({
  workContents,
  onChange,
  onSave,
  onFinish,
  errors,
}) {
  const [activeNumberPad, setActiveNumberPad] = useState(null);
  const [activeSelect, setActiveSelect] = useState(null);
  const [options, setOptions] = useState([]);
  const [otherOptions, setOtherOptions] = useState([]);
  const [workPlaces, setWorkPlaces] = useState([]);
  const [modelTypes, setModelTypes] = useState([]);
  const [modelNumbers, setModelNumbers] = useState([]);
  const [workProcesses, setWorkProcesses] = useState([]);
  const [stopLosses, setStopLosses] = useState([]);
  const [selectedInterruptWork, setSelectedInterruptWork] = useState(null);
  const [selectedLossTab, setSelectedLossTab] = useState("popular");

  /**
   * 作業職場一覧、機種一覧、機番一覧、工程一覧、停止ロス作業一覧を取得する
   * @memberof WorkContentTable
   */
  useEffect(() => {
    const getWorkPlaces = async () => {
      const result = await constantService.getWorkPlaces();
      setWorkPlaces(result);
    };
    const getModelTypes = async () => {
      const result = await constantService.getModelTypes();
      setModelTypes(result);
    };
    const getModelNumbers = async () => {
      const result = await constantService.getModelNumbers();
      setModelNumbers(result);
    };
    const getWorkProcesses = async () => {
      const result = await constantService.getWorkProcesses();
      setWorkProcesses(result);
    };
    const getStopLosses = async () => {
      const result = await constantService.getStopLosses();
      setStopLosses(result);
    };
    getWorkPlaces();
    getModelTypes();
    getModelNumbers();
    getWorkProcesses();
    getStopLosses();
  }, []);
  /**
   * オプションの値を設定する
   * @memberof WorkContentTable
   */
  useEffect(() => {
    switch (activeSelect) {
      case "workplaceId":
        setOptions(workPlaces);
        break;
      case "workProcessId":
        setOptions(workProcesses);
        setOtherOptions(workProcesses);
        break;
      case "modelNumberId":
        setOptions(modelNumbers);
        setOtherOptions(modelNumbers);
        break;
      case "modelTypeId":
        setOptions(modelTypes);
        break;
      default:
        setOptions([]);
        setOtherOptions([]);
        break;
    }
  }, [activeSelect, workPlaces, workProcesses, modelNumbers, modelTypes]);
  /**
   * 項目の値を変更し、ダイアログを閉じる
   * @param {string} name 項目名
   * @param {string} value 値
   * @memberof NoloadWorkContentTable
   */
  const onInputChange = (name, value) => {
    onChange(name, value);
    setActiveNumberPad(null);
    setActiveSelect(null);
    setOptions([]);
    setOtherOptions([]);
  };
  /**
   * 停止ロスボタンの選択/未選択の状態を更新する
   * @param {number} value 作業ロスID
   * @memberof NoloadWorkContentTable
   */
  const onStopLossChange = (value) => {
    if ((workContents.stopLoss ?? []).includes(value)) {
      onChange(
        "stopLoss",
        workContents.stopLoss.filter((id) => id !== value),
      );
    } else {
      onChange("stopLoss", [...(workContents.stopLoss ?? []), value]);
    }
  };
  const dialogTitle =
    strings.WORK_CONTENT_TITLES.find(
      (o) => o.name === activeNumberPad || o.name === activeSelect,
    )?.title ?? "";
  const dialogSubTitle =
    strings.WORK_CONTENT_TITLES.find(
      (o) => o.name === activeNumberPad || o.name === activeSelect,
    )?.subtitle ?? "";

  const workTypeOption = strings.WORK_TYPE_OPTIONS.find(
    (o) => o.value === workContents.workType,
  );
  const workPlace = workPlaces.find(
    (o) => o.value === workContents.workplaceId,
  )?.label;
  const modelType = modelTypes.find(
    (o) => o.value === workContents.modelTypeId,
  )?.label;
  const workProcess = workProcesses.find(
    (o) => o.value === workContents.workProcessId,
  )?.label;
  const modelNumber = modelNumbers.find(
    (o) => o.value === workContents.modelNumberId,
  )?.label;

  const stopLossLabels = Object.values(stopLosses)
    .flat()
    .filter((loss) => (workContents.stopLoss ?? []).includes(loss.value))
    .map((loss) => loss.label);

  const splitDate = dateService
    .formatDBDatetime(workContents?.startedAt, "yyyy/MM/dd HH:mm")
    .split(" ");
  return (
    <>
      <section id="work-content-table" className={isMobile ? "mobile" : ""}>
        <div className="sheet-head">
          <div className="sheet-title">
            <span className="sheet-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 11h6M9 15h6M8 3h8l3 3v15H5V3h3Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <div>
              <h2 id="manualTitle">作業情報</h2>
              <p>入力漏れ・誤操作を防ぐため、保存前確認を行います</p>
            </div>
          </div>
          <div className="head-badges">
            <span className="status-badge" id="workStatus">
              作業中
            </span>
            <WorkBadge workType={workContents.workType} />
          </div>
        </div>

        <div className="detail-grid">
          <div className="label-cell">作業種別</div>
          <div className="value-cell">
            <span className={`work-type ${workTypeOption?.name}`}>
              {workTypeOption?.label}
            </span>
          </div>

          <div className="label-cell">作業開始日時</div>
          <div className="value-cell">
            <span className="date-line">
              <span className="date">{splitDate[0]}</span>
              <span className="time">{splitDate[1]}</span>
            </span>
          </div>

          <div className="label-cell">職場</div>
          <div className="value-cell">{workPlace}</div>

          <div className="label-cell">機種</div>
          <div className="value-cell">{modelType}</div>

          <div className="label-cell">工程</div>
          <div className="value-cell">{workProcess}</div>

          <div className="label-cell">機番</div>
          <div className="value-cell">{modelNumber}</div>

          <div className="label-cell">出来高</div>
          <div className="value-cell wide">
            <div className="yield-box">
              <span className="yield-current">
                開始時出来高：<strong>20</strong>
              </span>

              <div className="yield-field">
                <label forhtml="yieldInput">終了時出来高</label>
                <div className="yield-input-row">
                  <input
                    className="yield-input js-dirty"
                    id="yieldInput"
                    type="text"
                    inputMode="none"
                    readOnly
                    placeholder="例：50"
                    aria-label="終了時出来高"
                    value={workContents.endVolume}
                  />
                  <button
                    className="keypad-open-btn"
                    onClick={() => setActiveNumberPad("endVolume")}
                  >
                    テンキー
                  </button>
                </div>
              </div>

              {errors?.endVolume && (
                <span className="error" style={{ marginLeft: "10px" }}>
                  {errors?.endVolume}
                </span>
              )}
            </div>
          </div>

          <div className="label-cell">間接作業</div>
          <div className="value-cell">
            <div className="registered-box">
              <span className="registered-label">登録済み間接作業</span>
              <div className="button-list">
                <ChipButton title="間接作業1" />
              </div>
            </div>
          </div>

          <div className="label-cell">無負荷作業</div>
          <div className="value-cell">
            <div className="registered-box">
              <span className="registered-label">登録済み無負荷作業</span>
              <div className="button-list">
                <ChipButton title="無負荷作業1" />
              </div>
            </div>
          </div>

          <div className="label-cell">
            停止ロス時
            <br />
            不稼働時間
          </div>
          <div className="value-cell wide loss-area">
            <div className="loss-panel">
              <div className="loss-tabs" aria-label="停止ロス分類">
                {stopLossCategory.map((category) => (
                  <button
                    key={`loss-tab-button${category.value}`}
                    className={`loss-tab ${selectedLossTab === category.value ? "is-active" : ""}`}
                    onClick={() => setSelectedLossTab(category.value)}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              <div
                className="loss-items"
                id="lossItems"
                aria-label="停止ロス項目"
              >
                {stopLosses[selectedLossTab]?.map((stoploss) => (
                  <ChipButton
                    key={`loss-button${stoploss.value}`}
                    title={stoploss.label}
                    className={`loss-btn ${(workContents.stopLoss ?? []).includes(stoploss.value) ? "is-selected" : ""}`}
                    onClick={() => onStopLossChange(stoploss.value)}
                  />
                ))}
              </div>

              <div className="selected-panel">
                <p className="selected-title">選択中</p>
                <p
                  className={`selected-text ${stopLossLabels.length === 0 ? "empty" : ""}`}
                >
                  {stopLossLabels.join("、")}
                  {stopLossLabels.length === 0 && "未選択"}
                </p>
              </div>
            </div>
          </div>

          <div className="label-cell">備考</div>
          <div className="value-cell wide">
            <textarea
              className="memo-area js-dirty"
              id="memo"
              placeholder="備考を入力してください"
            ></textarea>
          </div>
        </div>

        <div className="bottom-actions">
          <OutlineButton title="入力内容を保存" icon="save" onClick={onSave} />
          <GreenButton title="作業を完了する" onClick={onFinish} icon="check" />
        </div>
      </section>

      <NumberPadDialog
        title={dialogTitle}
        subtitle={dialogSubTitle}
        name={activeNumberPad}
        value={workContents?.[activeNumberPad]}
        onChange={onInputChange}
        open={activeNumberPad !== null}
        onClose={() => setActiveNumberPad(null)}
      />
      <SelectDialog
        title={dialogTitle}
        subtitle={dialogSubTitle}
        name={activeSelect}
        value={workContents?.[activeSelect]}
        options={options}
        otherOptions={otherOptions}
        showKeypad={activeSelect === "modelTypeId"}
        onChange={onInputChange}
        open={activeSelect !== null}
        onClose={() => setActiveSelect(null)}
      />
      <InterruptWorkDetailDialod
        open={selectedInterruptWork != null}
        onClose={() => setSelectedInterruptWork(null)}
        interruptWorkId={selectedInterruptWork?.interruptWorkId}
        type={selectedInterruptWork?.type}
      />
    </>
  );
}
